/**
 * Phase 9.7.21 — 🎖️ Leaderboard Placement Share Modal
 * ---------------------------------------------------
 * One-click branded PNG for an artist's leaderboard placement.
 *
 * Reuses the proven Cloudinary `imageToDataUri` pipeline from
 * NowFeaturedShareModal so cover bytes always embed.
 */
import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { getProfilePhotoUrl } from "@/lib/mediaUrls";
import { getPublicShareUrl } from "@/lib/publicUrl";
import { bakeImage, exportNodeToBlob, downloadBlob, shareOrDownload } from "@/lib/shareExport";

const fmt = (n) => Number(n || 0).toLocaleString();

const formatDayLong = (key) => {
  if (!key) return "";
  try {
    return new Date(`${key}T00:00:00Z`).toLocaleDateString(undefined, {
      year: "numeric", month: "long", day: "numeric"
    });
  } catch { return key; }
};

const PlacementCard = ({ data, bakedAvatar, cardRef }) => {
  const avatar = bakedAvatar || data.avatar;
  const medal = data.rank === 1 ? "🥇" : data.rank === 2 ? "🥈" : data.rank === 3 ? "🥉" : "🏆";
  return (
    <div
      ref={cardRef}
      data-testid="placement-card"
      style={{
        width: 1080, height: 1080,
        position: "relative",
        background:
          "radial-gradient(120% 80% at 0% 0%, #4c1d95 0%, transparent 55%)," +
          "radial-gradient(120% 80% at 100% 100%, #ec4899 0%, transparent 60%)," +
          "linear-gradient(180deg, #0a0512 0%, #100319 60%, #07020d 100%)",
        color: "white",
        fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, 'Helvetica Neue', Arial",
        overflow: "hidden",
        boxSizing: "border-box",
        padding: 70,
      }}
    >
      <div style={{
        position: "absolute", top: -180, right: -180,
        width: 700, height: 700, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(250,204,21,0.35) 0%, rgba(250,204,21,0) 65%)",
        pointerEvents: "none",
      }} />
      <div style={{
        position: "absolute", bottom: -200, left: -180,
        width: 800, height: 800, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(168,85,247,0.35) 0%, rgba(168,85,247,0) 65%)",
        pointerEvents: "none",
      }} />

      {/* Title */}
      <div style={{ textAlign: "center" }}>
        <div style={{
          display: "inline-block",
          padding: "12px 28px", borderRadius: 999,
          background: "linear-gradient(90deg, #facc15 0%, #f59e0b 50%, #ec4899 100%)",
          color: "#1a0a00", fontWeight: 900, fontSize: 26, letterSpacing: 4,
          textTransform: "uppercase",
          boxShadow: "0 12px 40px rgba(250,204,21,0.40)",
        }}>
          🏆 Most Pushed on RGM Studio
        </div>
      </div>

      {/* Rank hero */}
      <div style={{ textAlign: "center", marginTop: 70 }}>
        <div style={{ fontSize: 200, lineHeight: 1, textShadow: "0 0 40px rgba(250,204,21,0.55)" }}>
          {medal}
        </div>
        <div style={{
          marginTop: -30,
          fontSize: 240, fontWeight: 900, lineHeight: 1,
          background: "linear-gradient(90deg, #fde68a 0%, #fbbf24 50%, #ec4899 100%)",
          WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
        }}>
          #{data.rank}
        </div>
        <div style={{ marginTop: 10, fontSize: 32, fontWeight: 800, color: "#fde68a", letterSpacing: 4, textTransform: "uppercase" }}>
          Most Pushed Artist Today
        </div>
      </div>

      {/* Artist */}
      <div style={{
        marginTop: 60,
        display: "flex", alignItems: "center", gap: 24,
        padding: "20px 28px",
        background: "linear-gradient(135deg, rgba(250,204,21,0.18), rgba(236,72,153,0.18))",
        border: "2px solid rgba(250,204,21,0.55)",
        borderRadius: 22,
      }}>
        <img
          src={avatar}
          alt=""
          crossOrigin="anonymous"
          style={{ width: 110, height: 110, borderRadius: "50%", objectFit: "cover", border: "4px solid #facc15", flexShrink: 0 }}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 52, fontWeight: 900, color: "white", lineHeight: 1.05 }}>
            {data.artist_name}
          </div>
          <div style={{ marginTop: 8, fontSize: 22, fontWeight: 700, color: "#fde68a", letterSpacing: 2, textTransform: "uppercase" }}>
            🚀 In Rotation on RGM Studio
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div style={{ marginTop: 32, display: "flex", gap: 18 }}>
        {[
          { icon: "🏆", label: "Reach 24h", value: fmt(data.delta_24h), color: "#fde68a" },
          { icon: "👁", label: "Impressions", value: fmt(data.impressions), color: "#fbcfe8" },
        ].map((m) => (
          <div key={m.label} style={{
            flex: 1, minWidth: 0,
            background: "linear-gradient(135deg, rgba(250,204,21,0.18), rgba(236,72,153,0.18))",
            border: "2px solid rgba(250,204,21,0.45)",
            borderRadius: 22, padding: "18px 24px",
          }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: m.color, letterSpacing: 2, textTransform: "uppercase" }}>
              {m.icon} {m.label}
            </div>
            <div style={{ marginTop: 4, fontSize: 50, fontWeight: 900, color: "white" }}>
              {m.value}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ position: "absolute", bottom: 50, left: 0, right: 0, textAlign: "center" }}>
        <div style={{ fontSize: 22, color: "#fef3c7", opacity: 0.85, letterSpacing: 1, marginBottom: 10 }}>
          {formatDayLong(data.day_key)}
        </div>
        <div style={{
          display: "inline-block",
          padding: "10px 24px", borderRadius: 999,
          background: "linear-gradient(90deg, rgba(250,204,21,0.18) 0%, rgba(236,72,153,0.18) 100%)",
          border: "1px solid rgba(250,204,21,0.5)",
          fontSize: 24, fontWeight: 900, letterSpacing: 5, color: "#fde68a",
        }}>
          RGM STUDIO · rgmstudio.org
        </div>
      </div>
    </div>
  );
};

const LeaderboardPlacementShareModal = ({ open, onOpenChange, row, dayKey }) => {
  const cardRef = useRef(null);
  const [bakedAvatar, setBakedAvatar] = useState(null);
  const [avatarWarn, setAvatarWarn] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!open || !row) return;
    let cancelled = false;
    setAvatarWarn(false);
    (async () => {
      // Always feed the avatar through the strict media-URL helper so a
      // null/missing photo deterministically lands on the branded
      // placeholder (no blank circle in the export).
      const avatarUrl = getProfilePhotoUrl({
        profile_photo: row.artist_photo,
        picture: row.picture,
      }, 'thumb');
      const r = await bakeImage(avatarUrl, { kind: "avatar" });
      if (cancelled) return;
      setBakedAvatar(r.uri);
      // Warn the artist only if THEY had a real photo and we had to
      // fall back. (No warning when they had no photo at all — the
      // placeholder is the correct render in that case.)
      if (row.artist_photo && r.source === "fallback") setAvatarWarn(true);
    })();
    return () => { cancelled = true; };
  }, [open, row]);

  if (!row) return null;

  const data = {
    rank: row.rank,
    artist_name: row.artist_name,
    avatar: row.artist_photo,
    delta_24h: row.delta_24h,
    impressions: row.impressions,
    day_key: dayKey,
  };

  const filename = `RGM-Most-Pushed-Rank-${row.rank}-${(row.artist_name || "artist").replace(/[^a-z0-9-_ ]/gi, "").trim()}.png`;
  const shareUrl = getPublicShareUrl("/leaderboard/today");

  const exportBlob = async () => {
    if (!cardRef.current) return null;
    return await exportNodeToBlob(cardRef.current);
  };

  const handleDownload = async () => {
    setExporting(true);
    try {
      const blob = await exportBlob();
      if (!blob) throw new Error("export-failed");
      downloadBlob(blob, filename);
      toast.success("Placement card saved 🎉");
    } catch {
      toast.error("Couldn't render the card. Try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleNativeShare = async () => {
    setExporting(true);
    try {
      const blob = await exportBlob();
      if (!blob) throw new Error("export-failed");
      const result = await shareOrDownload({
        blob, filename,
        title: `🏆 #${row.rank} Most Pushed on RGM Studio`,
        text: `${row.artist_name} ranked #${row.rank} most pushed on RGM Studio today 🚀`,
        url: shareUrl,
      });
      if (result === "shared") toast.success("Shared!");
      else if (result === "downloaded") toast.success("Saved 🎉");
      else toast.error("Share isn't available here — try Download instead.");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Share isn't available here — try Download instead.");
    } finally {
      setExporting(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(
        `🏆 ${row.artist_name} ranked #${row.rank} most pushed on RGM Studio today 🚀\n${shareUrl}`
      );
      toast.success("Caption + link copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md bg-black border border-amber-400/40 max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="placement-share-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2 text-lg">
            <span>🎖️</span><span>Share Placement</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-amber-200/70">
            #{row.rank} placement card — premium social brag.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto pr-1 mt-2">
          <div className="flex items-center justify-center bg-black/40 rounded-xl border border-amber-400/20 p-3" data-testid="placement-preview-frame">
            <div style={{ width: 320, height: 320, position: "relative", overflow: "hidden", borderRadius: 12 }}>
              <div style={{ width: 1080, height: 1080, transform: `scale(${320 / 1080})`, transformOrigin: "top left" }}>
                <PlacementCard data={data} bakedAvatar={bakedAvatar} cardRef={null} />
              </div>
            </div>
          </div>

          {avatarWarn && (
            <div className="mt-3 p-2.5 rounded-md bg-amber-500/15 border border-amber-400/40 text-[11px] text-amber-100" data-testid="placement-avatar-warning">
              ⚠️ Profile photo couldn't be embedded — your PNG will show the default RGM avatar. Re-upload the artist's profile photo to fix.
            </div>
          )}
        </div>

        <div className="pt-3 mt-2 border-t border-amber-400/20 flex flex-wrap gap-2 justify-end">
          <Button variant="outline" className="text-amber-100 border-amber-400/40" onClick={handleCopyLink} data-testid="placement-copy-link">
            🔗 Copy link
          </Button>
          <Button variant="outline" className="text-amber-100 border-amber-400/40" onClick={handleDownload} disabled={exporting} data-testid="placement-download">
            ⬇️ {exporting ? "Rendering…" : "Download"}
          </Button>
          <Button className="bg-gradient-to-r from-amber-500 to-pink-500 hover:from-amber-400 hover:to-pink-400 text-white font-bold" onClick={handleNativeShare} disabled={exporting} data-testid="placement-share">
            📲 {exporting ? "Rendering…" : "Share placement"}
          </Button>
        </div>

        {/* Offscreen full-size export target */}
        <div aria-hidden style={{ position: "fixed", left: -99999, top: 0, opacity: 0.01, pointerEvents: "none" }}>
          <PlacementCard data={data} bakedAvatar={bakedAvatar} cardRef={cardRef} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LeaderboardPlacementShareModal;
